import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@ohpen/react-utils';
import { VERSION as ENSO_VERSION, type AboutInfo } from '@ohpen/enso-ui';

import { PmpConfigContext } from '@pmp/contexts';
import { useFeatureToggle } from './useFeatureToggle';
import { fetchAgentVersion, type AgentBuildInfo } from '../assistant/agentVersion';
import type { PmpAssistantAppConfig } from '../assistant/agentTransport';

/**
 * What the About entry reports: versions first, in the order a reader cares about (the portal,
 * then what it talks to), then where they are running.
 */
export const useAboutInfo = (): AboutInfo => {
  const { t } = useTranslation();
  const config = useConfig<PmpAssistantAppConfig>();
  const pmpConfig = useContext(PmpConfigContext);
  const assistantEnabled = useFeatureToggle(['enableAssistant']);
  const [agentBuild, setAgentBuild] = useState<AgentBuildInfo | null>(null);

  // Only asked for where the assistant actually runs, so an environment without it neither shows
  // the row nor calls an agent that is not there.
  useEffect(() => {
    if (!assistantEnabled) {
      setAgentBuild(null);
      return;
    }
    const controller = new AbortController();
    fetchAgentVersion(config.APP?.agentUrl, controller.signal).then(setAgentBuild);
    return () => controller.abort();
  }, [assistantEnabled, config.APP?.agentUrl]);

  return useMemo(
    () => ({
      items: [
        { label: t('global.labels.pmpVersion'), value: config.VERSION || '-' },
        { label: t('global.labels.brsVersion'), value: pmpConfig?.brsVersion || '-' },
        ...(agentBuild ? [{ label: t('global.labels.agentVersion'), value: agentBuild.version }] : []),
        { label: t('global.labels.ensoUiVersion'), value: ENSO_VERSION },
        { label: t('global.labels.stage'), value: config.STAGE || '-' },
        { label: t('global.labels.client'), value: config.CLIENT || '-' },
      ],
    }),
    [agentBuild, config, pmpConfig, t],
  );
};
